import { useEffect, useState } from 'react';
import { BookOpen, Question, EyeSlash, Info, Key, ShieldWarning, Stack, Warning } from '@phosphor-icons/react';

import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Link } from '../lib/routing';
import { cn } from '@/lib/utils';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useIsAdminViewer } from '../hooks/use-is-admin-viewer';
import {
  fetchCouverture,
  fetchLecture,
  fetchPerteMaterialisation,
  fetchTitreEnDesaccord,
  materializeDocument,
  messageMaterialisation,
  runExtractionWindow,
  type CouvertureCorpus,
  type CouvertureCoupleRow,
  type DocumentLectureRow,
  type DocumentPerteRow,
  type LectureCorpus,
  type MotifLecture,
  type PerimetresCorpus,
  type PerteMaterialisationCorpus,
  type TitreEnDesaccordRow,
  type ProgrammeInvisibleRow,
  type VerdictCouverture,
} from '../lib/curriculum';

const CARD = 'bg-card border border-border rounded-[16px] p-[14px]';
const VOIE_ABSENTE = 'voie non renseignée';

const VERDICT_LABEL: Record<VerdictCouverture, string> = {
  couvert: 'Couvert',
  range_ailleurs: 'Rangé sous une autre voie',
  manquant: 'Manquant',
};

export default function AdminCouverture() {
  const isAdminViewer = useIsAdminViewer();
  const [data, setData] = useState<CouvertureCorpus | null>(null);
  const [lecture, setLecture] = useState<LectureCorpus | null>(null);
  const [perte, setPerte] = useState<PerteMaterialisationCorpus | null>(null);
  const [desaccords, setDesaccords] = useState<TitreEnDesaccordRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reload = () =>
    Promise.all([
      fetchCouverture(),
      fetchLecture(),
      fetchPerteMaterialisation(),
      fetchTitreEnDesaccord(),
    ])
      .then(([couverture, lu, perdu, desaccord]) => {
        setData(couverture);
        setLecture(lu);
        setPerte(perdu);
        setDesaccords(desaccord);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));

  useEffect(() => {
    void reload();
  }, []);

  const t = data?.totaux;
  const p = data?.perimetres;

  return (
    <>
      <HeaderContent width="xl" showBurger backTo="/admin" backLabel="Admin">
        <h1 className={HEADER_TITLE_CLASS}>Couverture du corpus</h1>
      </HeaderContent>
      <main className="max-w-[1240px] mx-auto px-4 pb-16 pt-4 flex flex-col gap-4">
        {error && <Alert variant="destructive">{error}</Alert>}
        {note && <Alert>{note}</Alert>}

        <LimiteDeLaVue couples={t?.couples ?? null} manquants={t?.manquants ?? null} />

        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2">
          <Kpi label="Couples suivis" value={t?.couples} />
          <Kpi label="Couverts" value={t?.couverts} perimetre={t?.couples} perimetreLabel="couples suivis" />
          <Kpi label="Rangés sous une autre voie" value={t?.ranges_ailleurs} perimetre={t?.couples} perimetreLabel="couples suivis" alerte={(t?.ranges_ailleurs ?? 0) > 0} />
          <Kpi label="Manquants" value={t?.manquants} perimetre={t?.couples} perimetreLabel="couples suivis" alerte={(t?.manquants ?? 0) > 0} />
          <Kpi label="Autorité usurpée" value={t?.autorite_sans_programme} perimetre={t?.couples} perimetreLabel="couples suivis" alerte={(t?.autorite_sans_programme ?? 0) > 0} />
          {/* INDICATEUR, PAS ALARME — et c'est le contrôle du corpus qui le dit :
              « le nombre bouge à chaque abrogation ou ingestion, sa valeur seule
              ne dit pas s'il y a faute, c'est sa VARIATION qui compte ».
              Alarmer sur sa valeur absolue le laissait allumé en permanence, et
              une alarme toujours allumée n'est pas une alarme.
              Certains conflits sont d'ailleurs LÉGITIMES : le programme d'EMC de
              2024 s'applique au cycle 3 sans que celui de 2020 y ait été abrogé.
              Le corpus reflète alors correctement un état du droit lui-même
              redondant — servir les deux n'est pas une erreur d'ingestion. */}
          <Kpi label="Couples à plusieurs textes en vigueur" value={t?.couples_en_conflit} perimetre={t?.couples} perimetreLabel="couples suivis" />
          {/* INDICATEUR lui aussi : ce n'est pas un défaut du corpus mais une
              limite du MODÈLE — il n'existe ni axe de série ni axe d'année au
              lycée. Aucun geste depuis cette page ne peut le réparer ; il attend
              une décision de modèle de données. Alarmer dessus laisserait un
              voyant rouge allumé jusqu'à cet arbitrage, et un voyant qui ne
              s'éteint jamais cesse d'être lu. La limite reste dite en toutes
              lettres dans la section, où elle est utile. */}
          <Kpi label="Clés sans axe (lycée)" value={t?.couples_sans_axe} perimetre={t?.couples} perimetreLabel="couples suivis" />
          <Kpi label="Documents coupés en deux" value={t?.programmes_coupes_en_deux} perimetre={p?.programmes} perimetreLabel="programmes" alerte={(t?.programmes_coupes_en_deux ?? 0) > 0} />
          <Kpi label="Items perdus par la coupure" value={t?.items_coupes_en_deux} perimetre={p?.items} perimetreLabel="items" alerte={(t?.items_coupes_en_deux ?? 0) > 0} />
          <Kpi label="Programmes transversaux" value={(t?.programmes_sans_cycle ?? 0) - (t?.programmes_coupes_en_deux ?? 0)} perimetre={p?.programmes} perimetreLabel="programmes" />
        </div>

        {!data ? (
          <div className={cn(CARD, 'text-muted-foreground')}>Chargement…</div>
        ) : (
          <>
            <AutoriteUsurpee couples={data.couples.filter((c) => c.autorite_sans_programme)} />
            <Invisible
              programmes={data.invisibles}
              canMutate={isAdminViewer}
              onRematerialise={async (documentId) => {
                setError(null);
                setNote(null);
                try {
                  const m = messageMaterialisation(await materializeDocument(documentId));
                  if (m.erreur) {
                    setError(m.erreur);
                    return;
                  }
                  setNote(m.note);
                  await reload();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                }
              }}
            />
            <ClesInexploitables cles={data.cles_inexploitables} />
            <TitreEnDesaccord rows={desaccords} />
            {perte && <PerteALaMaterialisation perte={perte} perimetres={data.perimetres} />}
            {lecture && (
              <CeQuiNaPasEteLu
                lecture={lecture}
                perimetres={data.perimetres}
                canMutate={isAdminViewer}
                onExtrait={async (documentId) => {
                  setError(null);
                  setNote(null);
                  try {
                    const r = await runExtractionWindow(documentId);
                    setNote(
                      r.error
                        ? `${r.error}${r.detail ? ` — ${r.detail}` : ''}`
                        : r.page_start
                          ? `Extraction : pages ${r.page_start}–${r.page_end} · ${r.item_count ?? 0} items` +
                            (r.more_windows ? ' · fenêtres restantes' : ' · document terminé')
                          : (r.message ?? 'Rien à extraire.'),
                    );
                    await reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
                onRematerialise={async (documentId) => {
                  setError(null);
                  setNote(null);
                  try {
                    const m = messageMaterialisation(await materializeDocument(documentId));
                    if (m.erreur) {
                      setError(m.erreur);
                      return;
                    }
                    setNote(m.note);
                    await reload();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  }
                }}
              />
            )}
            <TableauDesCouples couples={data.couples} />
          </>
        )}
      </main>
    </>
  );
}

/* Le bandeau le plus important de la page, et il ne se replie pas. Cette vue
   n'énumère que les couples dont le corpus porte déjà une trace : un programme
   matérialisé, ou une fenêtre d'extraction qui a déclaré le couple. Un
   enseignement dont RIEN n'a jamais été ingéré ne laisse aucune trace et n'a
   donc aucune ligne ici — l'absence de « manquant » ne prouve pas la complétude.
   Le référentiel ne permet pas de faire mieux : `subjects` ne porte pas de
   `cycle_id`, rien ne déclare quelle matière s'enseigne à quel cycle, et une
   grille en produit cartésien afficherait « physique-chimie au CP ». */
function LimiteDeLaVue({ couples, manquants }: { couples: number | null; manquants: number | null }) {
  return (
    <div className={cn(CARD, 'bg-naia-info-bg')}>
      <div className="flex gap-2 items-start">
        <Info size={18} weight="bold" className="mt-[1px] shrink-0" />
        <div className="text-[13px] leading-relaxed">
          <div className="font-extrabold mb-1">Ce que cette vue ne peut pas voir</div>
          <p className="mb-2">
            Elle n'énumère que les {couples ?? '—'} couples dont le corpus porte déjà une trace — un
            programme matérialisé, ou une fenêtre d'extraction qui a déclaré le couple.{' '}
            <strong>Un enseignement dont rien n'a jamais été ingéré n'a aucune ligne ici.</strong>{' '}
            {manquants === 0
              ? "« 0 manquant » signifie donc « aucun trou parmi ce que le corpus touche », jamais « le corpus est complet »."
              : null}
          </p>
          <p className="mb-2">
            Un couple est <strong>matière × cycle × voie × langue</strong>. La langue en fait partie
            depuis le 06/09 : sans elle, « langues-vivantes / cycle 4 / commun » réunissait
            22 langues et 25 programmes en une seule ligne. Le périmètre compte{' '}
            <strong>tous les programmes, y compris ceux dont l'autorité a été retirée</strong> — un
            couple servi seulement par des programmes démis est un vrai manque, et il doit se voir.
            Un compteur mesuré sur les seuls programmes faisant autorité rendra donc un nombre plus
            petit : les deux sont justes, ils répondent à deux questions.
          </p>
          <p className="text-muted-foreground">
            La table <code>subjects</code> ne déclare pas quel enseignement existe à quel cycle. Une
            grille matière × cycle en produit cartésien donnerait « physique-chimie au CP » et
            noierait le vrai manque dans le bruit. Les textes détectés comme absents par la veille du
            Bulletin officiel se suivent sur{' '}
            <Link to="/admin/programmes-manquants" className="text-naia-blue font-bold">
              Programmes manquants
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  );
}

function AutoriteUsurpee({ couples }: { couples: CouvertureCoupleRow[] }) {
  return (
    <section className={CARD}>
      <EnTete
        icone={<ShieldWarning size={18} weight="bold" />}
        titre="Autorité usurpée"
        compte={couples.length}
        explication="Ces couples ont un programme faisant autorité, mais tous leurs documents servants sont de nature « accompagnement ». Un document de ressources y répond avec la même autorité qu'un arrêté. La nature vient de la provenance du document, jamais de son titre ni de son texte."
      />
      {couples.length === 0 ? (
        <Vide>Aucun couple servi uniquement par de l'accompagnement.</Vide>
      ) : (
        <TableWrap
          entetes={['Matière', 'Cycle', 'Voie', 'Items', 'Documents servants']}
          lignes={couples.map((c) => (
            <tr key={cleCouple(c)} className="border-t border-border align-top">
              <td className="py-2 pr-3 font-bold">{c.subject_label}</td>
              <td className="py-2 pr-3">{c.cycle_label}</td>
              <td className="py-2 pr-3">{c.voie ?? <Discret>{VOIE_ABSENTE}</Discret>}</td>
              <td className="py-2 pr-3 font-mono">{c.items}</td>
              <td className="py-2 pr-3">
                <ul className="flex flex-col gap-1">
                  {c.documents.map((d) => (
                    <li key={d.id ?? d.title} className="flex flex-wrap gap-x-2 items-baseline">
                      <span>{d.title ?? <Discret>document supprimé</Discret>}</span>
                      <BadgeNature nature={d.nature} />
                      <span className="text-muted-foreground font-mono text-[11px]">{d.items} items</span>
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        />
      )}
    </section>
  );
}

/* Un programme sans cycle ne remonte dans AUCUNE requête par cycle ni par
   niveau : ses items existent, sont comptés partout ailleurs, et ne répondront
   jamais à un enseignant. Aucun écran ne le disait avant celui-ci. */
function Invisible({
  programmes,
  canMutate,
  onRematerialise,
}: {
  programmes: ProgrammeInvisibleRow[];
  canMutate: boolean;
  onRematerialise: (documentId: string) => Promise<void>;
}) {
  const [enCours, setEnCours] = useState<string | null>(null);
  const items = programmes.reduce((n, p) => n + p.items, 0);
  /* Le compte du titre porte sur les DÉFAUTS, pas sur les lignes affichées :
     les transversaux sont listés pour information, ils n'appellent rien. Un
     titre qui annonce « (5) » quand aucun n'est un défaut alarme sur du
     légitime — la faute que ce lot vient de corriger un cran plus bas. */
  const coupes = programmes.filter((p) => p.coupe_en_deux).length;

  return (
    <section className={CARD}>
      <EnTete
        icone={<EyeSlash size={18} weight="bold" />}
        titre="L'invisible — programmes sans cycle"
        compte={coupes}
        explication={`Un programme sans cycle est invisible à toute requête par cycle ou par niveau. Mais tous ne sont pas des défauts : un document TRANSVERSAL — cadre des compétences numériques, éducation aux médias, parcours inclusif — ne relève légitimement d'aucun cycle. Le vrai défaut est le document COUPÉ EN DEUX, dont une moitié porte son cycle et l'autre non : c'est la signature du cycle omis par le modèle, qui a rendu 614 items invisibles le 05/09. ${items} items au total, dont ceux des coupures seuls appellent une action.`}
      />
      {programmes.length === 0 ? (
        <Vide>Aucun programme sans cycle.</Vide>
      ) : (
        <TableWrap
          entetes={['Matière', 'Voie', 'Document source', 'Items', 'Nature du cas', 'Action']}
          lignes={programmes.map((p) => (
            <tr key={p.programme_id} className="border-t border-border align-top">
              <td className="py-2 pr-3 font-bold">{p.subject_label}</td>
              <td className="py-2 pr-3">{p.voie ?? <Discret>{VOIE_ABSENTE}</Discret>}</td>
              <td className="py-2 pr-3">
                <div className="flex flex-wrap gap-x-2 items-baseline">
                  <span>{p.document_title ?? <Discret>sans document source</Discret>}</span>
                  <BadgeNature nature={p.document_nature} />
                </div>
              </td>
              <td className="py-2 pr-3 font-mono">{p.items}</td>
              <td className="py-2 pr-3">
                {p.coupe_en_deux ? (
                  <span className="text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-naia-info-bg text-foreground">
                    coupé en deux
                  </span>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    transversal — sans cycle légitimement
                  </span>
                )}
              </td>
              <td className="py-2 pr-3">
                {!p.coupe_en_deux ? (
                  /* Un transversal n'a rien à réparer : offrir un bouton — même
                     bloqué — le ferait passer pour un problème en attente.
                     C'est la règle déjà appliquée aux rejets nominaux de « Ce
                     qui n'a pas été lu », qui manquait ici. */
                  <Discret>rien à réparer</Discret>
                ) : p.document_id === null ? (
                  <Discret>document source absent</Discret>
                ) : !canMutate ? (
                  <Discret>lecture seule</Discret>
                ) : (
                  <BoutonRematerialiser
                    documentId={p.document_id}
                    enCours={enCours === p.document_id}
                    bloquee={p.rematerialisation_bloquee}
                    motif={p.rematerialisation_motif}
                    onLance={async (id) => {
                      setEnCours(id);
                      try {
                        await onRematerialise(id);
                      } finally {
                        setEnCours(null);
                      }
                    }}
                  />
                )}
              </td>
            </tr>
          ))}
        />
      )}
    </section>
  );
}

/* Ni un manque ni une couverture : une TROISIÈME situation, et la confondre
   avec « manquant » enverrait ré-ingérer un texte déjà possédé. Le contenu est
   là, rangé sous une clé qu'aucune requête d'enseignant ne formule. */
function ClesInexploitables({ cles }: { cles: CouvertureCorpus['cles_inexploitables'] }) {
  return (
    <section className={CARD}>
      <EnTete
        icone={<Key size={18} weight="bold" />}
        titre="Clés inexploitables"
        compte={cles.length}
        explication="L'extraction a rangé du contenu sous une clé qu'aucune requête d'enseignant ne formule. Ce n'est pas un texte manquant — le contenu est en base. Deux causes à ne pas confondre : « non déclaré » veut dire que le modèle s'est tu, « hors référentiel » qu'il a produit un code inconnu. La première est de loin la plus fréquente, et elle se répare en ré-extrayant, pas en touchant au référentiel."
      />
      {cles.length === 0 ? (
        <Vide>Toutes les clés extraites existent au référentiel.</Vide>
      ) : (
        <TableWrap
          entetes={['Matière', 'Cycle', 'Voie', 'Ce qui cloche', 'Fenêtres', 'Documents']}
          lignes={cles.map((c, i) => (
            <tr key={`${c.subject_code}-${c.cycle_code}-${c.voie}-${i}`} className="border-t border-border align-top">
              <td className="py-2 pr-3 font-mono">{c.subject_code ?? <Discret>non déclarée</Discret>}</td>
              <td className="py-2 pr-3 font-mono">{c.cycle_code ?? <Discret>non déclaré</Discret>}</td>
              <td className="py-2 pr-3 font-mono">{c.voie ?? <Discret>{VOIE_ABSENTE}</Discret>}</td>
              <td className="py-2 pr-3">
                <div className="flex gap-1 flex-wrap">
                  {c.champs.map((champ) => (
                    <span key={champ} className="text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-muted text-muted-foreground">
                      {champ}
                    </span>
                  ))}
                </div>
              </td>
              <td className="py-2 pr-3 font-mono">{c.fenetres}</td>
              <td className="py-2 pr-3 font-mono">{c.documents}</td>
            </tr>
          ))}
        />
      )}
    </section>
  );
}

/* La perte SILENCIEUSE. Un document peut matérialiser ses programmes, afficher
   un compte parfaitement normal, et avoir laissé tomber des thèmes en chemin :
   `materialize_curriculum` résout la matière par thème et abandonne celui dont
   le code ne résout pas. Rien, nulle part, ne le disait.

   Le titre porte sur la perte PARTIELLE et non sur le total, et c'est délibéré :
   mesuré le 05/09, 77 % des items non matérialisés viennent de documents qui
   n'ont rien produit du tout — des ressources sur l'école inclusive qui n'ont
   jamais porté de programme. Les compter comme « perdus » affirmerait qu'on a
   perdu du programme. Le total reste affiché, mais ventilé, jamais seul. */
function PerteALaMaterialisation({
  perte,
  perimetres,
}: {
  perte: PerteMaterialisationCorpus;
  perimetres: PerimetresCorpus;
}) {
  const [toutMontrer, setToutMontrer] = useState(false);
  const t = perte.totaux;
  const visibles = toutMontrer
    ? perte.documents
    : perte.documents.filter((d) => d.perte === 'partielle');

  return (
    <section className={CARD}>
      <EnTete
        icone={<Warning size={18} weight="bold" />}
        titre="Perte à la matérialisation — partielle et silencieuse"
        compte={t.partielle_documents}
        explication="Ces documents ont matérialisé leurs programmes ET laissé tomber des thèmes en chemin : la matière d'un thème ne résolvait pas au référentiel, le thème est abandonné seul, et le document affiche un compte normal. Un document amputé et un document complet s'écrivent identiquement."
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2 mb-3">
        <Kpi label="Documents amputés" value={t.partielle_documents} perimetre={perimetres.documents} perimetreLabel="documents" alerte={t.partielle_documents > 0} />
        <Kpi label="Items perdus en silence" value={t.partielle_items} perimetre={perimetres.items} perimetreLabel="items du corpus" alerte={t.partielle_items > 0} />
        <Kpi
          label="Dont textes réglementaires"
          value={t.partielle_documents_programme}
          perimetre={t.partielle_documents}
          perimetreLabel="documents amputés"
          alerte={t.partielle_documents_programme > 0}
        />
        <Kpi label="Items perdus sur ces textes" value={t.partielle_items_programme} perimetre={perimetres.items} perimetreLabel="items du corpus" alerte={t.partielle_items_programme > 0} />
        <Kpi label="Thèmes perdus sans aucun item" value={t.themes_sans_items} perimetre={t.themes_perdus} perimetreLabel="thèmes perdus" />
      </div>

      {/* Le total, ventilé — jamais seul. Un nombre juste dans une colonne qui
          dit de quoi il est fait. */}
      <div className="border border-border rounded-[10px] p-3 mb-3 text-[13px]">
        <div className="font-extrabold mb-1.5">Le total, et de quoi il est fait</div>
        <ul className="flex flex-col gap-1 text-muted-foreground">
          <li>
            <strong className="text-foreground">{t.partielle_items} items</strong> perdus sur{' '}
            {t.partielle_documents} document(s) qui ont <strong>quand même matérialisé</strong> — la
            perte que rien d'autre ne montre.
          </li>
          <li>
            <strong className="text-foreground">{t.totale_items} items</strong> sur{' '}
            {t.totale_documents} document(s) qui n'ont <strong>rien produit du tout</strong>. Ce sont
            très majoritairement des ressources qui n'ont jamais porté de programme : les compter
            comme perdus dirait qu'on a perdu du programme. Ils se suivent dans « Ce qui n'a pas été
            lu ».
          </li>
          <li>
            Total brut : {t.items_perdus} items sur {t.themes_perdus} thèmes, {t.documents}{' '}
            documents.
          </li>
        </ul>
      </div>

      <p className="text-[13px] text-muted-foreground mb-3">
        <button
          type="button"
          onClick={() => setToutMontrer((v) => !v)}
          className="text-naia-blue font-bold underline cursor-pointer bg-transparent border-none p-0 font-[inherit] text-[13px]"
        >
          {toutMontrer
            ? "N'afficher que la perte partielle"
            : `Afficher aussi les ${t.totale_documents} documents qui n'ont rien produit`}
        </button>
      </p>

      {visibles.length === 0 ? (
        <Vide>Aucune perte à la matérialisation.</Vide>
      ) : (
        <TableWrap
          entetes={['Document', 'Perte', 'Programmes', 'Thèmes perdus', 'Items perdus', 'Matières non résolues']}
          lignes={visibles.map((d) => <LignePerte key={d.document_id} document={d} />)}
        />
      )}
    </section>
  );
}

function LignePerte({ document: d }: { document: DocumentPerteRow }) {
  return (
    <tr className="border-t border-border align-top">
      <td className="py-2 pr-3 max-w-[320px]">
        <div className="font-bold">{d.title ?? <Discret>sans titre</Discret>}</div>
        <div className="mt-1">
          <BadgeNature nature={d.nature} />
        </div>
      </td>
      <td className="py-2 pr-3">
        <span
          className={cn(
            'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full whitespace-nowrap',
            d.perte === 'partielle' ? 'bg-naia-info-bg text-foreground' : 'bg-muted text-muted-foreground',
          )}
        >
          {d.perte === 'partielle' ? 'partielle — silencieuse' : 'totale — déjà signalée'}
        </span>
      </td>
      <td className="py-2 pr-3 font-mono">{d.programmes}</td>
      <td className="py-2 pr-3 font-mono">
        {d.themes_perdus}
        {d.themes_sans_items > 0 && (
          <div className="text-[11px] text-muted-foreground">{d.themes_sans_items} sans item</div>
        )}
      </td>
      <td className="py-2 pr-3 font-mono">{d.items_perdus}</td>
      <td className="py-2 pr-3">
        <div className="flex gap-1 flex-wrap">
          {d.sujets_non_resolus.map((sujet) => (
            <span
              key={sujet}
              className="text-[11px] font-extrabold px-[9px] py-[3px] rounded-full bg-muted text-muted-foreground"
            >
              {sujet}
            </span>
          ))}
        </div>
      </td>
    </tr>
  );
}

/* Une SUSPICION, jamais un verdict — et le mot compte. Rien n'est corrigé
   automatiquement, rien n'est même affirmé : le titre d'un document nomme une
   matière du référentiel autre que celle sous laquelle il est rangé, et ne
   nomme pas celle-ci. On confronte deux choses que la base possède déjà, on ne
   déduit aucun vocabulaire.

   C'est la seule famille de la page qui ne CRIE pas : un document mal rangé
   affiche des compteurs parfaitement sains. Le bloqué se voit partout, le mal
   rangé nulle part. */
function TitreEnDesaccord({ rows }: { rows: TitreEnDesaccordRow[] }) {
  if (rows.length === 0) return null;
  const fortes = rows.filter((r) => !r.suspicion_faible);

  return (
    <section className={CARD}>
      <EnTete
        icone={<Question size={18} weight="bold" />}
        titre="Titre et matière en désaccord"
        compte={fortes.length}
        explication="Le titre de ces documents nomme une matière du référentiel autre que celle sous laquelle ils sont rangés — et ne nomme pas celle-ci. C'est une suspicion à vérifier, jamais un verdict : rien n'est corrigé ni affirmé. Un document mal rangé est la seule anomalie de cette page qui ne se voit nulle part ailleurs, puisque tous ses compteurs sont normaux."
      />
      <TableWrap
        entetes={['Document', 'Rangé sous', 'Le titre évoque', 'Items', 'Force']}
        lignes={rows.map((r) => (
          <tr key={`${r.programme_id}-${r.titre_evoque}`} className="border-t border-border align-top">
            <td className="py-2 pr-3 max-w-[340px] font-bold">{r.title}</td>
            <td className="py-2 pr-3 font-mono">{r.subject_label}</td>
            <td className="py-2 pr-3 font-mono">{r.titre_evoque_label}</td>
            <td className="py-2 pr-3 font-mono">{r.items}</td>
            <td className="py-2 pr-3">
              {r.suspicion_faible ? (
                <span className="text-[11px] text-muted-foreground">
                  faible — le libellé « {r.subject_label} » est un sigle, qu'un titre épelant la
                  discipline ne peut pas contenir
                </span>
              ) : (
                <span className="text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full bg-naia-info-bg text-foreground">
                  à vérifier
                </span>
              )}
            </td>
          </tr>
        ))}
      />
    </section>
  );
}

const MOTIF_LABEL: Record<MotifLecture, string> = {
  programme_sterile: 'texte réglementaire sans contenu',
  pages_jamais_lues: 'pages jamais lues',
  nature_inconnue_sans_contenu: 'nature inconnue, aucun contenu',
  fenetres_en_echec: 'fenêtres en échec',
  ignore: 'ignoré',
  rejet_probable: 'rejet probable',
};

/* La famille que les comptes de fin de lot ne voyaient pas : un document peut
   être compté « extrait » en ayant perdu des pages, échoué des fenêtres, ou
   n'avoir rien produit du tout. Le contrôle bloquant du corpus compte ces cas ;
   il ne dit pas LESQUELS. Cette section les nomme. */
function CeQuiNaPasEteLu({
  lecture,
  perimetres,
  canMutate,
  onRematerialise,
  onExtrait,
}: {
  lecture: LectureCorpus;
  perimetres: PerimetresCorpus;
  canMutate: boolean;
  onRematerialise: (documentId: string) => Promise<void>;
  onExtrait: (documentId: string) => Promise<void>;
}) {
  const [toutMontrer, setToutMontrer] = useState(false);
  const t = lecture.totaux;
  const visibles = toutMontrer
    ? lecture.documents
    : lecture.documents.filter((d) => d.demande_attention);

  return (
    <section className={CARD}>
      <EnTete
        icone={<BookOpen size={18} weight="bold" />}
        titre="Ce qui n'a pas été lu"
        compte={t.documents_en_attention}
        explication="Un document compté « extrait » peut avoir perdu des pages, échoué des fenêtres, ou n'avoir rien produit. Trois causes de « zéro programme » qui ne se confondent pas : un texte de nature « programme » sans contenu est une anomalie certaine ; un accompagnement sans contenu est un rejet probable ; une nature inconnue sans contenu reste inconnue — surtout pas saine."
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2 mb-3">
        <Kpi label="Texte réglementaire stérile" value={t.programme_sterile} perimetre={perimetres.documents} perimetreLabel="documents" alerte={t.programme_sterile > 0} />
        <Kpi label="Nature inconnue, sans contenu" value={t.nature_inconnue_sans_contenu} perimetre={perimetres.documents} perimetreLabel="documents" alerte={t.nature_inconnue_sans_contenu > 0} />
        <Kpi label="Pages jamais lues" value={t.pages_jamais_lues} perimetre={perimetres.pages} perimetreLabel="pages du corpus" alerte={t.pages_jamais_lues > 0} />
        <Kpi label="Fenêtres en échec" value={t.fenetres_en_echec} perimetre={perimetres.fenetres} perimetreLabel="fenêtres" alerte={t.fenetres_en_echec > 0} />
        <Kpi label="Documents ignorés" value={t.ignores} perimetre={perimetres.documents} perimetreLabel="documents" />
        <Kpi label="Rejets probables" value={t.rejets_probables} perimetre={perimetres.documents} perimetreLabel="documents" />
      </div>

      <p className="text-[13px] text-muted-foreground mb-3">
        {t.rejets_probables} document(s) d'accompagnement n'ont produit aucun programme : c'est
        nominal, une ressource ne porte pas de texte réglementaire. Ils sont comptés, pas alarmés —
        les afficher au même rang noierait ce qui compte.{' '}
        <button
          type="button"
          onClick={() => setToutMontrer((v) => !v)}
          className="text-naia-blue font-bold underline cursor-pointer bg-transparent border-none p-0 font-[inherit] text-[13px]"
        >
          {toutMontrer ? "N'afficher que ce qui demande attention" : 'Afficher tous les documents'}
        </button>
      </p>

      {visibles.length === 0 ? (
        <Vide>Aucun document ne demande attention.</Vide>
      ) : (
        <TableWrap
          entetes={['Document', 'Pages lues', 'Fenêtres', 'Programmes', 'Ce qui cloche', 'Action']}
          lignes={visibles.map((d) => (
            <LigneLecture
              key={d.document_id}
              document={d}
              canMutate={canMutate}
              onRematerialise={onRematerialise}
              onExtrait={onExtrait}
            />
          ))}
        />
      )}
    </section>
  );
}

function LigneLecture({
  document: d,
  canMutate,
  onRematerialise,
  onExtrait,
}: {
  document: DocumentLectureRow;
  canMutate: boolean;
  onRematerialise: (documentId: string) => Promise<void>;
  onExtrait: (documentId: string) => Promise<void>;
}) {
  const [enCours, setEnCours] = useState(false);

  return (
    <tr className="border-t border-border align-top">
      <td className="py-2 pr-3 max-w-[320px]">
        <div className="font-bold">{d.title ?? <Discret>sans titre</Discret>}</div>
        <div className="mt-1 flex gap-1.5 flex-wrap items-baseline">
          <BadgeNature nature={d.nature} />
          {d.curriculum_status !== 'extracted' && (
            <span className="text-[11px] text-muted-foreground">{d.curriculum_status}</span>
          )}
        </div>
      </td>
      <td className="py-2 pr-3 font-mono whitespace-nowrap">
        {d.pages_lues}/{d.page_count}
        {d.pages_jamais_lues > 0 && (
          <div className="text-[11px] text-muted-foreground">{d.pages_jamais_lues} jamais lue(s)</div>
        )}
      </td>
      <td className="py-2 pr-3 font-mono whitespace-nowrap">
        {d.fenetres}
        {d.fenetres_en_echec > 0 && (
          <div className="text-[11px] text-muted-foreground">{d.fenetres_en_echec} en échec</div>
        )}
      </td>
      <td className="py-2 pr-3 font-mono">
        {d.programmes}
        {d.items > 0 && <div className="text-[11px] text-muted-foreground">{d.items} items</div>}
        {/* La portée du geste, avant le geste. Un document consolidé en sert
            douze d'un coup — c'est ce qu'on n'avait pas avant d'appuyer. */}
        {d.couples_touches > 0 && (
          <div className="text-[11px] text-muted-foreground">
            {d.couples_touches} couple(s) touché(s)
            {d.couples_non_jugeables > 0 && `, dont ${d.couples_non_jugeables} non jugeable(s)`}
          </div>
        )}
      </td>
      <td className="py-2 pr-3">
        <div className="flex gap-1 flex-wrap">
          {d.motifs.map((m) => (
            <span
              key={m}
              className={cn(
                'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full',
                m === 'programme_sterile' || m === 'nature_inconnue_sans_contenu'
                  ? 'bg-naia-info-bg text-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {MOTIF_LABEL[m]}
            </span>
          ))}
        </div>
      </td>
      <td className="py-2 pr-3">
        {d.action === 'aucune' ? (
          <Discret>rien à réparer</Discret>
        ) : !canMutate ? (
          <Discret>lecture seule</Discret>
        ) : d.action === 'extraire' ? (
          <div className="max-w-[260px]">
            <Button
              size="sm"
              variant="outline"
              disabled={enCours}
              onClick={async () => {
                setEnCours(true);
                try {
                  await onExtrait(d.document_id);
                } finally {
                  setEnCours(false);
                }
              }}
            >
              {enCours ? 'Extraction…' : 'Extraire la fenêtre suivante'}
            </Button>
            <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
              Reprend à la première page non lue. Re-matérialiser ne lirait aucune page.
            </p>
          </div>
        ) : (
          <BoutonRematerialiser
            documentId={d.document_id}
            enCours={enCours}
            bloquee={d.rematerialisation_bloquee}
            motif={d.rematerialisation_motif}
            onLance={async (id) => {
              setEnCours(true);
              try {
                await onRematerialise(id);
              } finally {
                setEnCours(false);
              }
            }}
          />
        )}
      </td>
    </tr>
  );
}

function TableauDesCouples({ couples }: { couples: CouvertureCoupleRow[] }) {
  const [verdictFiltre, setVerdictFiltre] = useState<VerdictCouverture | 'tous'>('tous');
  const visibles = verdictFiltre === 'tous' ? couples : couples.filter((c) => c.verdict === verdictFiltre);

  return (
    <section className={CARD}>
      <EnTete
        icone={<Stack size={18} weight="bold" />}
        titre="Couverture par couple"
        compte={couples.length}
        explication="Un couple est « couvert » dès qu'un programme faisant autorité s'y rattache avec au moins un item. « Rangé sous une autre voie » veut dire que le texte est possédé mais classé sous une clé voisine : c'est la clé qu'il faut corriger, pas un texte à retrouver."
      />
      <AxesManquantsAuLycee couples={couples} />
      <div className="flex gap-1.5 flex-wrap mb-3">
        {(['tous', 'couvert', 'range_ailleurs', 'manquant'] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setVerdictFiltre(v)}
            className={cn(
              'min-h-[36px] px-3 rounded-full text-[12px] font-extrabold border border-border cursor-pointer',
              verdictFiltre === v ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground',
            )}
          >
            {v === 'tous' ? 'Tous' : VERDICT_LABEL[v]}
            <span className="ml-1.5 font-mono">
              {v === 'tous' ? couples.length : couples.filter((c) => c.verdict === v).length}
            </span>
          </button>
        ))}
      </div>
      {visibles.length === 0 ? (
        <Vide>Aucun couple pour ce filtre.</Vide>
      ) : (
        <TableWrap
          entetes={['Matière', 'Cycle', 'Voie', 'Langue', 'Verdict', 'Programmes', 'Items', 'Documents servants']}
          lignes={visibles.map((c) => (
            <tr key={cleCouple(c)} className="border-t border-border align-top">
              <td className="py-2 pr-3 font-bold">{c.subject_label}</td>
              <td className="py-2 pr-3">{c.cycle_label}</td>
              <td className="py-2 pr-3">{c.voie ?? <Discret>{VOIE_ABSENTE}</Discret>}</td>
              <td className="py-2 pr-3 font-mono">
                {c.language_code ?? <Discret>toutes langues</Discret>}
              </td>
              <td className="py-2 pr-3">
                <BadgeVerdict verdict={c.verdict} />
                {c.verdict === 'range_ailleurs' && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    présent sous « {c.voie_reelle ?? VOIE_ABSENTE} »
                  </div>
                )}
                {c.autorite_sans_programme && (
                  <div className="text-[11px] text-muted-foreground mt-1">accompagnement seul</div>
                )}
                {c.en_conflit && (
                  <div className="text-[11px] text-muted-foreground mt-1">{c.programmes} textes concurrents</div>
                )}
                {c.coordonnee_sans_axe && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    clé sans année ni série
                  </div>
                )}
              </td>
              <td className="py-2 pr-3 font-mono">{c.programmes}</td>
              <td className="py-2 pr-3 font-mono">{c.items}</td>
              <td className="py-2 pr-3">
                <ul className="flex flex-col gap-1">
                  {c.documents.length === 0 ? (
                    <Discret>aucun</Discret>
                  ) : (
                    c.documents.map((d) => (
                      <li key={d.id ?? d.title} className="flex flex-wrap gap-x-2 items-baseline">
                        <span>{d.title ?? <Discret>document supprimé</Discret>}</span>
                        <BadgeNature nature={d.nature} />
                      </li>
                    ))
                  )}
                </ul>
              </td>
            </tr>
          ))}
        />
      )}
    </section>
  );
}

/* Le bouton porte son propre refus, avec le motif à côté. Un bouton désactivé
   sans explication se lit comme une panne ; celui-ci dit ce qu'il évite. */
function BoutonRematerialiser({
  documentId,
  enCours,
  bloquee,
  motif,
  onLance,
}: {
  documentId: string;
  enCours: boolean;
  bloquee: boolean;
  motif: string | null;
  onLance: (documentId: string) => Promise<void>;
}) {
  if (bloquee) {
    return (
      <div className="max-w-[260px]">
        <Button size="sm" variant="outline" disabled>
          Re-matérialiser
        </Button>
        <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{motif}</p>
      </div>
    );
  }
  return (
    <Button size="sm" variant="outline" disabled={enCours} onClick={() => onLance(documentId)}>
      {enCours ? 'Re-matérialisation…' : 'Re-matérialiser'}
    </Button>
  );
}

/* Nomme une limite du MODÈLE, sans prétendre la résoudre. Dire lesquels de ces
   couples mélangent effectivement deux années demanderait de déduire l'année et
   la série des titres de documents — c'est-à-dire d'inventer un axe que la base
   n'a pas, exactement ce que le bandeau du produit cartésien refuse de faire en
   tête de page. */
function AxesManquantsAuLycee({ couples }: { couples: CouvertureCoupleRow[] }) {
  const concernes = couples.filter((c) => c.coordonnee_sans_axe);
  if (concernes.length === 0) return null;
  const items = concernes.reduce((n, c) => n + c.items, 0);

  return (
    <div className="border border-border rounded-[10px] p-3 mb-3 bg-naia-info-bg text-[13px] leading-relaxed">
      <div className="font-extrabold mb-1">Au lycée, la clé ne porte ni l'année ni la série</div>
      <p className="mb-1.5">
        Un seul cycle « lycée » couvre la seconde, la première et la terminale, alors que les textes
        du lycée sont <strong>annuels</strong> ; et la voie « technologique » réunit STI2D, STL,
        ST2S, STD2A, STMG, S2TMD et STHR.{' '}
        <strong>
          {concernes.length} couple(s) du lycée sont servis par plusieurs textes ({items} items).
        </strong>
      </p>
      <p className="text-muted-foreground">
        Leurs titres les distinguent — la colonne « Documents servants » les affiche — mais la clé,
        non. Cette page ne déduit pas l'année ni la série des titres : ce serait inventer un axe que
        la base ne porte pas. Aux cycles 1 à 4, rien n'est signalé : un programme de cycle 4 couvre
        légitimement la 5e, la 4e et la 3e.
      </p>
    </div>
  );
}

const cleCouple = (c: CouvertureCoupleRow) => `${c.subject_code}-${c.cycle_code}-${c.voie ?? ''}`;

function EnTete({
  icone,
  titre,
  compte,
  explication,
}: {
  icone: React.ReactNode;
  titre: string;
  compte: number;
  explication: string;
}) {
  return (
    <div className="mb-3">
      <div className="flex gap-2 items-center font-extrabold mb-1">
        {icone}
        <span>
          {titre} ({compte})
        </span>
      </div>
      <p className="text-[13px] text-muted-foreground leading-relaxed">{explication}</p>
    </div>
  );
}

function TableWrap({ entetes, lignes }: { entetes: string[]; lignes: React.ReactNode[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[13px] min-w-[640px]">
        <thead>
          <tr className="text-left text-muted-foreground">
            {entetes.map((e) => (
              <th key={e} className="pb-2 pr-3 font-bold">
                {e}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{lignes}</tbody>
      </table>
    </div>
  );
}

function BadgeVerdict({ verdict }: { verdict: VerdictCouverture }) {
  const style =
    verdict === 'couvert'
      ? 'bg-accent text-primary'
      : verdict === 'range_ailleurs'
        ? 'bg-naia-info-bg text-foreground'
        : 'bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full inline-block',
        style,
      )}
    >
      {VERDICT_LABEL[verdict]}
    </span>
  );
}

function BadgeNature({ nature }: { nature: string | null }) {
  const libelle = nature ?? 'nature à déterminer';
  return (
    <span
      className={cn(
        'text-[11px] font-extrabold uppercase tracking-[0.05em] px-[9px] py-[3px] rounded-full whitespace-nowrap',
        nature === 'programme' ? 'bg-accent text-primary' : 'bg-muted text-muted-foreground',
      )}
    >
      {libelle}
    </span>
  );
}

/* Un compteur nu se lit comme un changement d'état alors qu'il peut n'être qu'un
   changement de PÉRIMÈTRE : le 05/09, une requalification de 43 documents a fait
   entrer 50 programmes dans une métrique sans qu'aucune autorité ne bouge, et
   deux rôles ont cherché deux heures une écriture qui n'existait pas.
   « 194 » alarme, « 194 sur 111 976 » informe ; « 38 » rassure, « 38 sur 92 »
   non. Le dénominateur n'est donc pas décoratif — il porte le sens. */
function Kpi({
  label,
  value,
  perimetre,
  perimetreLabel,
  alerte,
}: {
  label: string;
  value: number | undefined;
  perimetre?: number;
  perimetreLabel?: string;
  alerte?: boolean;
}) {
  const part =
    value !== undefined && perimetre !== undefined && perimetre > 0
      ? Math.round((value / perimetre) * 1000) / 10
      : null;
  return (
    <div className={cn('border border-border rounded-[12px] px-[10px] py-2 bg-card', alerte && 'bg-naia-info-bg')}>
      <div className="text-[9px] font-black tracking-[0.9px] text-muted-foreground uppercase">{label}</div>
      <div className="text-[18px] font-extrabold mt-0.5">
        {value ?? '—'}
        {perimetre !== undefined && (
          <span className="text-[12px] font-bold text-muted-foreground ml-1">sur {perimetre}</span>
        )}
      </div>
      {perimetre !== undefined && (
        <div className="text-[10px] text-muted-foreground">
          {part !== null ? `${part} %` : '—'}
          {perimetreLabel ? ` des ${perimetreLabel}` : ''}
        </div>
      )}
    </div>
  );
}

const Vide = ({ children }: { children: React.ReactNode }) => (
  <p className="text-muted-foreground text-[13px]">{children}</p>
);

const Discret = ({ children }: { children: React.ReactNode }) => (
  <span className="text-muted-foreground italic">{children}</span>
);
